import React from 'react';
import classNames from 'classnames';
import { Form } from 'antd';
import { FormInstance } from 'antd/lib/form';
import { Moment } from 'moment';
import { TimeZoneFormat } from '@shared/utils/constants';
import { toMoment } from '@shared/utils/date';
import {
	fromISOTimezone,
	timeZoneOffsetLabel,
	toISOTimezoneInstant,
} from '@shared/utils/timezones';
import { getPortById } from '@api/utils/ports';
import DatePicker, { DatePickerProps, DatePickerValue } from '@client/components/DatePicker';
import Select from '@client/components/Select';
import datePickerStyles from '@client/components/styles/DatePicker.module.css';
import styles from './PortAwareDatePicker.module.css';

export const TIME_ZONE_FORMAT_OPTIONS = [
	{ label: 'UTC', value: TimeZoneFormat.UTC },
	{ label: 'Local Time', value: TimeZoneFormat.LocalTime },
];

export const getPortTimezone = (
	portId: number | null | undefined,
) => {
	if (portId == null) {
		return undefined;
	}

	return getPortById(portId)?.timezone;
};

export const toPortDateTimeInputValue = (
	dateTime: DatePickerValue,
	timeZoneFormat: TimeZoneFormat,
	timezone?: string | null,
): Moment | null => {
	if (dateTime == null) {
		return null;
	}

	return fromISOTimezone(
		toMoment(dateTime).toISOString(true),
		timeZoneFormat,
		timezone,
	);
};

export const normalizePortDateTime = (
	dateTime: DatePickerValue,
	timeZoneFormat: TimeZoneFormat,
	timezone?: string | null,
): Moment | null => {
	if (dateTime == null) {
		return null;
	}

	return toISOTimezoneInstant(
		toMoment(dateTime),
		timeZoneFormat,
		timezone,
	);
};

type PortAwareDatePickerProps = DatePickerProps & {
	portId?: number | null;
	timezone?: string | null;
	timeZoneFormat?: TimeZoneFormat;
};

export const PortAwareDatePicker = ({
	portId,
	timezone: timezoneProp,
	timeZoneFormat = TimeZoneFormat.UTC,
	value,
	fullWidth = true,
	time = false,
	className,
	variant,
	...props
}: PortAwareDatePickerProps) => {
	const timezone = timezoneProp ?? getPortTimezone(portId);
	const offsetLabel = timeZoneOffsetLabel(
		value == null ? null : toMoment(value),
		timezone,
		timeZoneFormat,
	);

	return (
		<div
			className={classNames(datePickerStyles.wrapper, {
				[datePickerStyles.fullWidth]: fullWidth,
			})}
		>
			{(time && offsetLabel) && (
				<Select
					variant={variant}
					key={offsetLabel}
					className={classNames(
						datePickerStyles.timezonePicker,
						timeZoneFormat === TimeZoneFormat.LocalTime && styles.localTimeOffsetLabel,
					)}
					disabled
					suffixIcon={null}
					options={[{ label: offsetLabel, value: offsetLabel }]}
					value={offsetLabel}
					popupMatchSelectWidth={false}
				/>
			)}
			<div style={{ flex: 1, minWidth: 0 }}>
				<DatePicker
					{...props}
					time={time}
					variant={variant}
					className={classNames(datePickerStyles.datePicker, className)}
					fullWidth={fullWidth}
					showTimezone={false}
					value={value}
				/>
			</div>
		</div>
	);
};

type PortAwareFormDatePickerProps = Omit<PortAwareDatePickerProps, 'portId' | 'timeZoneFormat'> & {
	formInstance: FormInstance<any>;
	portFieldName: string;
	timeZoneFormatFieldName: string;
	defaultTimeZoneFormat: TimeZoneFormat;
};

export const PortAwareFormDatePicker = ({
	formInstance,
	portFieldName,
	timeZoneFormatFieldName,
	defaultTimeZoneFormat,
	timezone,
	...props
}: PortAwareFormDatePickerProps) => {
	const portId = Form.useWatch(portFieldName, formInstance);
	const timeZoneFormat = Form.useWatch(timeZoneFormatFieldName, formInstance) ?? defaultTimeZoneFormat;

	return (
		<PortAwareDatePicker
			{...props}
			portId={portId}
			timezone={timezone}
			timeZoneFormat={timeZoneFormat}
		/>
	);
};
